import { Component, inject, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AnalysisService } from '../../../core/services/analysis.service';
import { ProjectService } from '../../../core/services/project.service';
import { UserService } from '../../../core/services/user.service';
import { Subscription, finalize, timeout, Observable } from 'rxjs';

import { FormatDurationPipe } from '../../../shared/pipes/format-duration.pipe';

@Component({
    selector: 'app-analysis',
    standalone: true,
    imports: [CommonModule, FormsModule, FormatDurationPipe],
    templateUrl: './analysis.component.html',
    styleUrl: './analysis.component.scss'
})
export class AnalysisComponent implements OnInit, OnDestroy {
    private analysisService = inject(AnalysisService);
    private projectService = inject(ProjectService);
    private userService = inject(UserService);
    private cdr = inject(ChangeDetectorRef);
    private currentRequest: Subscription | null = null;

    // Track expanded user cards using composite keys
    expandedUserCards = new Set<string>();

    // Default to 'weekly'
    selectedAnalysisType: string = 'daily';
    displayData: any = null;
    isLoading = false;
    startDate: string = '';
    endDate: string = '';

    // Projects Filter
    projects: any[] = [];
    selectedProjectId: string = ''; // Empty means 'All Projects'

    // Users Filter
    users: any[] = [];
    selectedUserId: string = ''; // Empty means 'All Users'

    showFilters = true;

    // Analysis types for the tab menu
    analysisTypes = [
        { id: 'daily', label: 'Daily Analysis', icon: 'fa-clock' },
        { id: 'weekly', label: 'Weekly Analysis', icon: 'fa-calendar-week' },
        { id: 'monthly', label: 'Monthly Analysis', icon: 'fa-calendar-days' }
    ];

    animationDirection: 'from-left' | 'from-right' = 'from-right';

    get currentTitle(): string {
        const type = this.analysisTypes.find(t => t.id === this.selectedAnalysisType);
        return type ? type.label : '';
    }

    get quickFilterOptions(): { label: string; filter: string }[] {
        switch (this.selectedAnalysisType) {
            case 'daily':
                return [
                    { label: 'Two days ago', filter: 'twoDaysAgo' },
                    { label: 'Yesterday', filter: 'yesterday' },
                    { label: 'Today', filter: 'today' }
                ];
            case 'weekly':
                return [
                    { label: 'Two weeks ago', filter: 'twoWeeksAgo' },
                    { label: 'Last week', filter: 'lastWeek' },
                    { label: 'This week', filter: 'thisWeek' }
                ];
            case 'monthly':
                return [
                    { label: 'Two months ago', filter: 'twoMonthsAgo' },
                    { label: 'Last month', filter: 'lastMonth' },
                    { label: 'This month', filter: 'thisMonth' }
                ];
            default:
                return [];
        }
    }

    ngOnInit() {
        this.loadProjects();
        this.loadUsers();
        // Set default date range based on initial selectedAnalysisType (daily)
        this.setQuickFilter('today');
    }

    ngOnDestroy() {
        if (this.currentRequest) {
            this.currentRequest.unsubscribe();
        }
    }

    loadProjects() {
        this.projectService.getAll().subscribe({
            next: (data: any[]) => {
                this.projects = data;
            },
            error: (err: any) => console.error('Failed to load projects', err)
        });
    }

    loadUsers() {
        this.userService.getAll().subscribe({
            next: (data: any[]) => {
                this.users = data;
            },
            error: (err: any) => console.error('Failed to load users', err)
        });
    }

    // Tab selection handler
    selectTab(type: string) {
        if (this.selectedAnalysisType === type) return;

        this.animationDirection = this.computeDirection(type);
        this.selectedAnalysisType = type;
        
        // Set appropriate default filter based on new tab type
        switch (type) {
            case 'daily':
                this.setQuickFilter('today');
                break;
            case 'weekly':
                this.setQuickFilter('thisWeek');
                break;
            case 'monthly':
                this.setQuickFilter('thisMonth');
                break;
            default:
                this.fetchData();
        }
    }

    private computeDirection(nextType: string): 'from-left' | 'from-right' {
        const currentIndex = this.analysisTypes.findIndex(t => t.id === this.selectedAnalysisType);
        const nextIndex = this.analysisTypes.findIndex(t => t.id === nextType);

        if (currentIndex === -1 || nextIndex === -1) return 'from-right';

        return nextIndex > currentIndex ? 'from-right' : 'from-left';
    }

    // Date change handler
    onDateChange() {
        this.fetchData();
    }

    onProjectChange() {
        this.fetchData();
    }

    onUserChange() {
        this.fetchData();
    }

    // Quick Date Filters
    setQuickFilter(filter: string) {
        const today = new Date();
        let start = new Date();
        let end = new Date();

        switch (filter) {
            case 'today':
                start = new Date(today);
                end = new Date(today);
                break;
            case 'yesterday': {
                const yesterday = new Date(today);
                yesterday.setDate(today.getDate() - 1);
                start = new Date(yesterday);
                end = new Date(yesterday);
                break;
            }
            case 'twoDaysAgo': {
                const twoDaysAgo = new Date(today);
                twoDaysAgo.setDate(today.getDate() - 2);
                start = new Date(twoDaysAgo);
                end = new Date(twoDaysAgo);
                break;
            }
            case 'thisWeek': {
                // Get start of this week (Monday)
                const dayOfWeek = today.getDay();
                const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday is 0, convert to days back to Monday
                start = new Date(today);
                start.setDate(today.getDate() - daysToMonday);
                // End of week is Sunday
                end = new Date(start);
                end.setDate(start.getDate() + 6);
                break;
            }
            case 'lastWeek': {
                // Get start of last week (Monday)
                const dayOfWeek = today.getDay();
                const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                start = new Date(today);
                start.setDate(today.getDate() - daysToMonday - 7); // Go back one more week
                // End of last week is Sunday
                end = new Date(start);
                end.setDate(start.getDate() + 6);
                break;
            }
            case 'twoWeeksAgo': {
                // Get start of two weeks ago (Monday)
                const dayOfWeek = today.getDay();
                const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                start = new Date(today);
                start.setDate(today.getDate() - daysToMonday - 14); // Go back two weeks
                // End of that week is Sunday
                end = new Date(start);
                end.setDate(start.getDate() + 6);
                break;
            }
            case 'thisMonth':
                start = new Date(today.getFullYear(), today.getMonth(), 1);
                end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                break;
            case 'lastMonth':
                start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                end = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            case 'twoMonthsAgo': {
                start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
                end = new Date(today.getFullYear(), today.getMonth() - 1, 0);
                break;
            }
        }

        // Format to YYYY-MM-DD for input[type="date"]
        this.startDate = start.toISOString().split('T')[0];
        this.endDate = end.toISOString().split('T')[0];

        // Trigger load
        this.fetchData();
    }

    // Unified fetch logic
    private fetchData() {
        // Cancel any pending request
        if (this.currentRequest) {
            this.currentRequest.unsubscribe();
            this.currentRequest = null;
        }

        if (!this.selectedAnalysisType) {
            this.isLoading = false;
            return;
        }

        this.displayData = null;
        // Don't set isLoading=true here to avoid flicker if synchronous error occurs before subscription

        let request;
        try {
            switch (this.selectedAnalysisType) {
                case 'weekly': request = this.analysisService.getWeeklyAnalysis(this.startDate, this.endDate, this.selectedProjectId, this.selectedUserId); break;
                case 'monthly': request = this.analysisService.getMonthlyAnalysis(this.startDate, this.endDate, this.selectedProjectId, this.selectedUserId); break;
                case 'daily': request = this.analysisService.getDailyEffort(this.startDate, this.endDate, this.selectedProjectId, this.selectedUserId); break;
                default:
                    this.isLoading = false;
                    return;
            }
        } catch (err) {
            console.error('Error creating analysis request:', err);
            this.isLoading = false;
            return;
        }

        if (!request) {
            this.isLoading = false;
            return;
        }

        this.isLoading = true;
        this.cdr.detectChanges();

        this.currentRequest = request.pipe(
            timeout(30000), // Timeout after 30 seconds to prevent infinite loading
            finalize(() => {
                this.isLoading = false;
                this.cdr.detectChanges();
                this.currentRequest = null;
            })
        ).subscribe({
            next: (data: any) => {
                const needsUserAttachment = (this.selectedAnalysisType === 'weekly' || this.selectedAnalysisType === 'daily' || this.selectedAnalysisType === 'monthly') && Array.isArray(data);

                if (needsUserAttachment) {
                    let processedData = this.attachUsersToProjects(data);
                    // Reverse daily data to show newest first
                    if (this.selectedAnalysisType === 'daily') {
                        processedData = [...processedData].reverse();
                    }
                    this.displayData = processedData;
                } else {
                    this.displayData = Array.isArray(data) ? [...data] : { ...data };
                }
            },
            error: (err: any) => {
                console.error(`Failed to load ${this.selectedAnalysisType}`, err);
                this.displayData = null;
            }
        });
    }

    // Helper for template
    isArray(obj: any): boolean {
        return Array.isArray(obj);
    }

    printReport() {
        window.print();
    }

    exportPdfReport() {
        if (!this.selectedAnalysisType) {
            console.warn('No analysis type selected');
            return;
        }

        // Convert single IDs to arrays if they exist
        const projectIds = this.selectedProjectId ? [this.selectedProjectId] : undefined;
        const userIds = this.selectedUserId ? [this.selectedUserId] : undefined;

        let exportRequest: Observable<Blob>;

        switch (this.selectedAnalysisType) {
            case 'daily':
                exportRequest = this.analysisService.exportDailyReportPdf(this.startDate, this.endDate, projectIds, userIds);
                break;
            case 'weekly':
                exportRequest = this.analysisService.exportWeeklyReportPdf(this.startDate, this.endDate, projectIds, userIds);
                break;
            case 'monthly':
                exportRequest = this.analysisService.exportMonthlyReportPdf(this.startDate, this.endDate, projectIds, userIds);
                break;
            default:
                console.warn('Unknown analysis type:', this.selectedAnalysisType);
                return;
        }

        this.isLoading = true;
        this.cdr.detectChanges();

        exportRequest.pipe(
            timeout(120000), // 2 minute timeout for PDF generation
            finalize(() => {
                this.isLoading = false;
                this.cdr.detectChanges();
            })
        ).subscribe({
            next: (response: any) => {
                const blob = response.body as Blob;
                const contentDisposition = response.headers.get('content-disposition');
                let fileName = `${this.selectedAnalysisType}-report.pdf`;
                
                // Extract filename from Content-Disposition header if available
                if (contentDisposition) {
                    const matches = contentDisposition.match(/filename[^;=\n]*=(['"]?)([^'"\n;]*)/i);
                    if (matches && matches[2]) {
                        fileName = matches[2];
                    }
                }
                
                this.downloadPdf(blob, fileName);
            },
            error: (err: any) => {
                console.error('Failed to export PDF report', err);
                alert('Failed to export PDF. Please check the console for details.');
            }
        });
    }

    private downloadPdf(blob: Blob, fileName: string) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    toggleRow(item: any) {
        item.expanded = !item.expanded;
    }

    toggleUserCard(parentItem: any, user: any) {
        const key = this.getUserCardKey(parentItem, user);
        if (this.expandedUserCards.has(key)) {
            this.expandedUserCards.delete(key);
        } else {
            this.expandedUserCards.add(key);
        }
    }

    isUserCardExpanded(parentItem: any, user: any): boolean {
        const key = this.getUserCardKey(parentItem, user);
        return this.expandedUserCards.has(key);
    }

    private getUserCardKey(parentItem: any, user: any): string {
        const parentId = this.getParentIdentifier(parentItem);
        return `${parentId}-${user.userId}`;
    }

    private getParentIdentifier(parentItem: any): string {
        switch (this.selectedAnalysisType) {
            case 'weekly':
                return `week-${parentItem.weekNumber}-${parentItem.year || new Date(parentItem.startDate).getFullYear()}`;
            case 'monthly':
                return `month-${parentItem.month}-${parentItem.year}`;
            case 'daily':
                return `date-${parentItem.date}`;
            default:
                return 'unknown';
        }
    }

    // Enrich breakdown with user-centric view showing projects under each user
    private attachUsersToProjects(data: any[]): any[] {
        return data.map(item => {
            // Create user-centric breakdown: users as cards with projects underneath
            const userBreakdownWithProjects = (item.personnelBreakdown || []).map((person: any) => ({
                userId: person.userId,
                userName: person.userName,
                totalHours: person.totalHours,
                projects: (person.projectBreakdown || []).map((pb: any) => ({
                    projectId: pb.projectId,
                    projectName: pb.projectName,
                    totalHours: pb.totalHours
                }))
            }));

            return {
                ...item,
                userBreakdown: userBreakdownWithProjects,
                // Keep original for when project filter is selected
                personnelBreakdown: item.personnelBreakdown
            };
        });
    }
}
